import { useCallback, useEffect, useRef, useState } from 'react';
import { AxiosResponse } from 'axios';
import {
  DocumentRequirementMutationResponse,
  DocumentRequirementsListResponse,
  ImportDocumentOptions,
  NewDocumentRequirementPayload,
  analyzeDocumentRequirements,
  createDocumentRequirement,
  ignoreDocumentRequirement,
  importDocumentRequirementFile,
  listDocumentManagerDocuments,
  listDocumentRequirements,
  removeDocumentRequirementFile,
  restoreDocumentRequirement,
  uploadDocumentRequirementFile,
} from 'services/actions/ai_management_document_requirements';
import {
  ANALYSIS_TIMED_OUT,
  DocumentManagerDocument,
  DocumentRequirement,
  DocumentRequirementsAnalysis,
  DocumentRequirementsProgress,
  EMPTY_PROGRESS,
  toAnalysis,
  toDocumentManagerDocument,
  toDocumentRequirement,
  toProgress,
} from 'services/ai_management_document_requirements';

interface State {
  id: number | null;
  requirements: DocumentRequirement[];
  progress: DocumentRequirementsProgress;
  analyzed: boolean;
  analysis: DocumentRequirementsAnalysis | null;
  error: boolean;
}

interface CatalogState {
  loading: boolean;
  loaded: boolean;
  error: boolean;
  documents: DocumentManagerDocument[];
}

const INITIAL_CATALOG: CatalogState = {
  loading: false,
  loaded: false,
  error: false,
  documents: [],
};

// Cada consulta roda a sincronização no bw-backend, que consulta o Analista por CNPJ.
export const ANALYSIS_POLL_DELAYS = [3000, 5000, 10000, 15000];
// Mesmo prazo do bw-backend para marcar a análise como esgotada.
export const ANALYSIS_POLL_LIMIT = 10 * 60 * 1000;

const emptyState = (id: number | null, error: boolean): State => ({
  id,
  requirements: [],
  progress: EMPTY_PROGRESS,
  analyzed: false,
  analysis: null,
  error,
});

const toState = (id: number, data: DocumentRequirementsListResponse): State => ({
  id,
  requirements: (data?.requirements ?? []).map(toDocumentRequirement),
  progress: toProgress(data?.progress),
  analyzed: !!data?.analyzed,
  analysis: toAnalysis(data?.analysis),
  error: false,
});

const isHidden = () => typeof document !== 'undefined' && document.hidden;

export const errorMessageOf = (err: unknown, fallback: string) => {
  const message = (err as { response?: { data?: { error?: string } } })?.response?.data?.error;
  return message || fallback;
};

export function useDocumentRequirements(biddingId: number | null) {
  const [state, setState] = useState<State>(() => emptyState(null, false));
  const [catalog, setCatalog] = useState<CatalogState>(INITIAL_CATALOG);
  const [analyzing, setAnalyzing] = useState(false);
  const [reanalyzing, setReanalyzing] = useState(false);
  const [hidden, setHidden] = useState(isHidden);
  const [tick, setTick] = useState(0);

  // Licitação atual, para descartar resposta que chega depois de trocar de licitação.
  const biddingRef = useRef(biddingId);
  biddingRef.current = biddingId;
  // Ações manuais concluídas: consulta iniciada antes de uma delas traz a lista antiga.
  const mutationsRef = useRef(0);
  const resumeRef = useRef(false);
  const pollRef = useRef({ biddingId: null as number | null, attempt: 0, startedAt: 0 });

  const restartPolling = useCallback((id: number) => {
    pollRef.current = { biddingId: id, attempt: 0, startedAt: Date.now() };
    // A lista acabou de chegar: uma volta de aba anterior não antecipa a primeira consulta.
    resumeRef.current = false;
  }, []);

  useEffect(() => {
    setCatalog(INITIAL_CATALOG);

    if (!biddingId) {
      setState(emptyState(null, false));
      return undefined;
    }

    let active = true;

    listDocumentRequirements(biddingId)
      .then((response) => {
        if (!active) return;
        const data = response?.data;
        if (!Array.isArray(data?.requirements)) {
          setState(emptyState(biddingId, true));
          return;
        }
        restartPolling(biddingId);
        setState(toState(biddingId, data));
      })
      .catch((err) => {
        console.error(`Erro ao listar as exigências de documentos da licitacao ${biddingId}:`, err);
        if (active) setState(emptyState(biddingId, true));
      });

    return () => {
      active = false;
    };
  }, [biddingId, restartPolling]);

  useEffect(() => {
    const onVisibilityChange = () => {
      if (!document.hidden) resumeRef.current = true;
      setHidden(document.hidden);
    };

    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => document.removeEventListener('visibilitychange', onVisibilityChange);
  }, []);

  const settled = state.id === biddingId;
  const processing = settled && state.analysis?.status === 'processing';

  useEffect(() => {
    if (!processing || !biddingId || hidden) return undefined;

    if (pollRef.current.biddingId !== biddingId) restartPolling(biddingId);
    const poll = pollRef.current;

    if (Date.now() - poll.startedAt >= ANALYSIS_POLL_LIMIT) {
      setState((prev) =>
        prev.id === biddingId ? { ...prev, analysis: ANALYSIS_TIMED_OUT } : prev,
      );
      return undefined;
    }

    const delay = resumeRef.current
      ? 0
      : ANALYSIS_POLL_DELAYS[Math.min(poll.attempt, ANALYSIS_POLL_DELAYS.length - 1)];
    resumeRef.current = false;
    let active = true;

    const timer = setTimeout(() => {
      poll.attempt += 1;
      const mutations = mutationsRef.current;

      listDocumentRequirements(biddingId)
        .then((response) => {
          if (!active) return;
          if (mutations !== mutationsRef.current) {
            resumeRef.current = true;
            return;
          }
          if (Array.isArray(response?.data?.requirements)) {
            setState(toState(biddingId, response.data));
          }
        })
        .catch((err) => {
          console.error(
            `Erro ao consultar a análise dos documentos da licitacao ${biddingId}:`,
            err,
          );
        })
        .finally(() => {
          if (active) setTick((value) => value + 1);
        });
    }, delay);

    return () => {
      active = false;
      clearTimeout(timer);
    };
  }, [processing, biddingId, hidden, tick, restartPolling]);

  const apply = useCallback(({ data }: AxiosResponse<DocumentRequirementMutationResponse>) => {
    mutationsRef.current += 1;
    const updated = toDocumentRequirement(data.requirement);

    setState((prev) => {
      const exists = prev.requirements.some((requirement) => requirement.id === updated.id);
      return {
        ...prev,
        requirements: exists
          ? prev.requirements.map((requirement) =>
              requirement.id === updated.id ? updated : requirement,
            )
          : [...prev.requirements, updated],
        progress: toProgress(data.progress),
      };
    });

    return updated;
  }, []);

  const withBidding = <T>(request: (id: number) => Promise<T>) =>
    biddingId ? request(biddingId) : Promise.reject(new Error('Licitação não carregada.'));

  const addRequirement = useCallback(
    (payload: NewDocumentRequirementPayload) =>
      withBidding((id) => createDocumentRequirement(id, payload)).then((response) => {
        setState((prev) => ({ ...prev, analyzed: true }));
        return apply(response);
      }),
    [biddingId, apply],
  );

  const ignore = useCallback(
    (requirementId: number) =>
      withBidding((id) => ignoreDocumentRequirement(id, requirementId)).then(apply),
    [biddingId, apply],
  );

  const restore = useCallback(
    (requirementId: number) =>
      withBidding((id) => restoreDocumentRequirement(id, requirementId)).then(apply),
    [biddingId, apply],
  );

  const uploadFile = useCallback(
    (requirementId: number, payload: { file: File; name: string; expiresOn?: string }) =>
      withBidding((id) => uploadDocumentRequirementFile(id, requirementId, payload)).then(apply),
    [biddingId, apply],
  );

  const importDocument = useCallback(
    (requirementId: number, documentId: string, options?: ImportDocumentOptions) =>
      withBidding((id) =>
        importDocumentRequirementFile(id, requirementId, documentId, options),
      ).then(apply),
    [biddingId, apply],
  );

  const removeFile = useCallback(
    (requirementId: number, fileId: number) =>
      withBidding((id) => removeDocumentRequirementFile(id, requirementId, fileId)).then(apply),
    [biddingId, apply],
  );

  const applyListFor = useCallback(
    (id: number, { data }: AxiosResponse<DocumentRequirementsListResponse>) => {
      if (biddingRef.current !== id) return;
      restartPolling(id);
      setState(toState(id, data));
    },
    [restartPolling],
  );

  const runAnalysis = useCallback(
    (setBusy: (busy: boolean) => void) => {
      setBusy(true);
      return withBidding((id) =>
        analyzeDocumentRequirements(id).then((response) => applyListFor(id, response)),
      ).finally(() => setBusy(false));
    },
    [biddingId, applyListFor],
  );

  const analyze = useCallback(() => runAnalysis(setAnalyzing), [runAnalysis]);
  const reanalyze = useCallback(() => runAnalysis(setReanalyzing), [runAnalysis]);

  const loadCatalog = useCallback(() => {
    if (!biddingId || catalog.loading || catalog.loaded) return;

    setCatalog((prev) => ({ ...prev, loading: true, error: false }));
    listDocumentManagerDocuments(biddingId)
      .then((response) => {
        const list = response?.data?.documents;
        setCatalog({
          loading: false,
          loaded: true,
          error: !Array.isArray(list),
          documents: Array.isArray(list) ? list.map(toDocumentManagerDocument) : [],
        });
      })
      .catch((err) => {
        console.error('Erro ao listar os documentos do Gerenciar Documentos:', err);
        setCatalog({ loading: false, loaded: false, error: true, documents: [] });
      });
  }, [biddingId, catalog.loading, catalog.loaded]);

  return {
    requirements: settled ? state.requirements : [],
    progress: settled ? state.progress : EMPTY_PROGRESS,
    analyzed: settled && state.analyzed,
    analysis: settled ? state.analysis : null,
    analyzing,
    reanalyzing,
    loading: !!biddingId && !settled,
    error: settled && state.error,
    catalog,
    loadCatalog,
    analyze,
    reanalyze,
    addRequirement,
    ignore,
    restore,
    uploadFile,
    importDocument,
    removeFile,
  };
}
